/**
 * Answer Statistics Chart Component
 *
 * Displays a bar chart of answer distribution for the current question.
 */

import type { AnswerStats } from '../../../types/liveQuiz';

interface AnswerStatsChartProps {
  stats: AnswerStats;
  options: Array<{ id: string; value: string; label: string; color: string }>;
  correctAnswer?: unknown;
}

const COLOR_BG_CLASSES: Record<string, string> = {
  red: 'bg-red-500',
  blue: 'bg-blue-500',
  yellow: 'bg-yellow-500',
  green: 'bg-green-500',
  purple: 'bg-purple-500',
  orange: 'bg-orange-500',
};

export function AnswerStatsChart({ stats, options, correctAnswer }: AnswerStatsChartProps) {
  const maxCount = Math.max(...Object.values(stats.distribution), 1);

  return (
    <div className="bg-white/5 rounded-xl p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-purple-300 uppercase tracking-wide">
          Answer Distribution
        </h3>
        <span className="text-sm text-gray-400">
          {stats.totalAnswered} of {stats.totalParticipants} answered
        </span>
      </div>

      <div className="space-y-3">
        {options.map((option) => {
          // Distribution may use option.id, option.value, or the label
          const count = stats.distribution[option.id] || stats.distribution[option.value] || 0;
          const percentage = stats.totalAnswered > 0 ? (count / stats.totalAnswered) * 100 : 0;
          const barWidth = maxCount > 0 ? (count / maxCount) * 100 : 0;
          // correctAnswer may be option.id or option.value - check both
          const isCorrect = correctAnswer !== undefined &&
            (correctAnswer === option.id || correctAnswer === option.value);

          return (
            <div key={option.id} className="space-y-1">
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <div
                    className={`w-4 h-4 rounded ${COLOR_BG_CLASSES[option.color] || 'bg-gray-500'}`}
                  />
                  <span className={`text-white ${isCorrect ? 'font-bold' : ''}`}>
                    {option.label}
                  </span>
                  {isCorrect && (
                    <span className="px-2 py-0.5 bg-green-500/30 text-green-400 text-xs rounded-full">
                      Correct
                    </span>
                  )}
                </div>
                <span className="text-gray-400">
                  {count} ({Math.round(percentage)}%)
                </span>
              </div>

              <div className="h-8 bg-white/10 rounded-lg overflow-hidden">
                <div
                  className={`h-full transition-all duration-500 ease-out flex items-center justify-end pr-2 ${
                    isCorrect ? 'bg-green-500' : COLOR_BG_CLASSES[option.color] || 'bg-gray-500'
                  }`}
                  style={{ width: `${barWidth}%` }}
                >
                  {count > 0 && (
                    <span className="text-white text-sm font-bold">{count}</span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Stats Summary */}
      <div className="mt-6 grid grid-cols-3 gap-4 pt-4 border-t border-white/10">
        <div className="text-center">
          <p className="text-2xl font-bold text-white">{stats.correctCount}</p>
          <p className="text-xs text-gray-400">Correct</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-white">
            {stats.averageResponseTimeMs > 0
              ? `${(stats.averageResponseTimeMs / 1000).toFixed(1)}s`
              : '-'}
          </p>
          <p className="text-xs text-gray-400">Avg Time</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-white">
            {stats.fastestResponseTimeMs > 0
              ? `${(stats.fastestResponseTimeMs / 1000).toFixed(1)}s`
              : '-'}
          </p>
          <p className="text-xs text-gray-400">Fastest</p>
        </div>
      </div>
    </div>
  );
}

export default AnswerStatsChart;
